import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Droplets, Clock, Zap, Shield, Truck, CheckCircle } from 'lucide-react';

const HERO_BG = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/fph3vzj9_IMG_0924.PNG";
const FLEET_IMG = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/3k8rtbma_IMG_0935.PNG";
const MANUFACTURING_IMG = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/xmkdi5p0_IMG_0874.jpg";
const GENERAL_IMG = "https://customer-assets.emergentagent.com/job_blast-clean/artifacts/zpqasujc_IMG_0925.PNG";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const stagger = {
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const benefits = [
  { icon: Droplets, title: 'Zero Water Usage', desc: 'No runoff, no corrosion risk, no drainage permits' },
  { icon: Clock, title: 'Reduced Downtime', desc: 'Clean equipment in-place without teardown' },
  { icon: Zap, title: 'Non-Conductive', desc: 'Safe for energized electrical systems' },
  { icon: Shield, title: 'No Secondary Waste', desc: 'Eliminates media cleanup costs' },
  { icon: Truck, title: 'Mobile Deployment', desc: 'Full on-site industrial service capability' },
  { icon: CheckCircle, title: '15+ Years Experience', desc: 'Deep mechanical & industrial expertise' },
];

const services = [
  { name: 'Dry Ice Blasting', desc: 'CO2 cleaning, waterless, non-abrasive' },
  { name: 'Fleet Cleaning', desc: 'Engine bays, undercarriages, chassis' },
  { name: 'Fire Damage Cleanup', desc: 'Soot, char, smoke residue removal' },
  { name: 'Mold Remediation', desc: 'Non-toxic mold removal' },
  { name: 'Equipment Restoration', desc: 'Like-new condition restoration' },
  { name: 'HVAC Cleaning', desc: 'Duct, coil, ventilation cleaning' },
];

export const HomePage = () => {
  return (
    <main className="bg-black" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center" data-testid="hero-section">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        >
          <div className="absolute inset-0 bg-black/70" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={stagger}
            className="max-w-3xl"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-block badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2 mb-6"
            >
              Minority & Woman-Owned Business
            </motion.span>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl sm:text-6xl md:text-7xl uppercase tracking-wider font-bold text-white mb-6"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Industrial Dry Ice Blasting Solutions
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-xl sm:text-2xl text-orange-500 font-bold uppercase tracking-wide mb-4"
              style={{ fontFamily: 'Bebas Neue, sans-serif' }}
            >
              Clean In-Place. Reduce Downtime. No Water. No Teardown.
            </motion.p>
            
            <motion.p 
              variants={fadeInUp}
              className="text-zinc-300 text-lg leading-relaxed mb-8 max-w-2xl"
            >
              23rd Industries delivers cutting-edge dry ice blasting services across the DFW Metroplex. 
              We clean your equipment without water, chemicals, or disassembly — minimizing downtime 
              and maximizing results.
            </motion.p>
            
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/contact" className="btn-primary px-8 py-4 text-lg inline-flex items-center justify-center gap-2" data-testid="hero-cta">
                Schedule Free On-Site Demo
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link to="/services" className="btn-outline px-8 py-4 text-lg inline-flex items-center justify-center" data-testid="hero-services-btn">
                View Services
              </Link>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 border-2 border-zinc-600 rounded-full flex items-start justify-center p-2"
          >
            <div className="w-1 h-3 bg-orange-500 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* What is Dry Ice Blasting */}
      <section className="py-24 bg-zinc-950" data-testid="what-is-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
                The Technology
              </span>
              <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                What is Dry Ice Blasting?
              </h2>
              <div className="space-y-4 text-zinc-300 leading-relaxed">
                <p>
                  Dry ice blasting uses compressed air to accelerate recycled CO2 pellets at high speed 
                  to clean surfaces. On impact, the dry ice sublimates (turns to gas), lifting contaminants 
                  without water, chemicals, or abrasive media.
                </p>
                <p>
                  The result: equipment cleaned in-place with zero secondary waste, zero water damage, 
                  and zero downtime for teardown.
                </p>
              </div>
              
              <div className="mt-8 space-y-4">
                {[
                  'Compressed air propels dry ice pellets at supersonic speed',
                  'Pellets sublimate on contact (solid → gas)',
                  'Thermal shock + kinetic energy lifts contaminants',
                  'No water, no sand, no chemicals left behind',
                  'Equipment stays in-place — no disassembly required'
                ].map((step, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <span className="flex-shrink-0 w-8 h-8 bg-orange-600 text-black font-bold flex items-center justify-center text-sm" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                      {i + 1}
                    </span>
                    <span className="text-zinc-300">{step}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="aspect-square relative">
                <img 
                  src={GENERAL_IMG} 
                  alt="Industrial dry ice blasting in action" 
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 border border-orange-500/30" />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-zinc-800 bg-zinc-950 p-6 flex flex-col justify-center">
                <span className="stat-number">15+</span>
                <span className="text-zinc-400 text-sm uppercase tracking-widest font-mono">Years Experience</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 bg-black" data-testid="benefits-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
              Why Choose Us
            </span>
            <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              The 23rd Advantage
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto">
              Our dry ice blasting technology delivers superior results while eliminating the drawbacks 
              of traditional cleaning methods.
            </p>
          </div>
          
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={stagger}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                variants={fadeInUp}
                className="service-card bg-zinc-950 border border-zinc-800 p-8"
                data-testid={`benefit-card-${i}`}
              >
                <benefit.icon className="w-10 h-10 text-orange-500 mb-6" />
                <h3 className="text-xl uppercase tracking-wide font-bold text-white mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {benefit.title}
                </h3>
                <p className="text-zinc-400">{benefit.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 bg-zinc-950" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 mb-16">
            <div>
              <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
                Our Services
              </span>
              <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                What We Do
              </h2>
            </div>
            <Link to="/services" className="btn-outline px-6 py-3 inline-flex items-center gap-2" data-testid="view-all-services">
              View All 15 Services
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="service-card bg-black border border-zinc-800 p-8 group"
                data-testid={`service-preview-${i}`}
              >
                <div className="flex items-start justify-between mb-4">
                  <span className="text-xs font-mono text-zinc-600 uppercase tracking-widest">0{i + 1}</span>
                  <ArrowRight className="w-5 h-5 text-zinc-700 group-hover:text-orange-500 transition-colors" />
                </div>
                <h3 className="text-xl uppercase tracking-wide font-bold text-white mb-2" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {service.name}
                </h3>
                <p className="text-zinc-400 text-sm">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="py-24 bg-black" data-testid="industries-preview">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
              Industries Served
            </span>
            <h2 className="text-4xl sm:text-5xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Who We Serve
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: 'Military & Government', img: MANUFACTURING_IMG },
              { name: 'Fleet Operations', img: FLEET_IMG },
              { name: 'Manufacturing', img: GENERAL_IMG },
            ].map((industry, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.15 }}
                className="industry-card aspect-[4/3] relative overflow-hidden group"
                data-testid={`industry-preview-${i}`}
              >
                <img 
                  src={industry.img} 
                  alt={industry.name}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
                <div className="industry-card-content absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-2xl uppercase tracking-wide font-bold text-white" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                    {industry.name}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <Link to="/industries" className="btn-outline px-8 py-3 inline-flex items-center gap-2" data-testid="view-all-industries">
              View All Industries
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Demo CTA Section */}
      <section className="py-24 bg-orange-600 relative overflow-hidden" data-testid="demo-cta-section">
        <div className="absolute inset-0 grid-pattern opacity-20" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl sm:text-5xl md:text-6xl uppercase tracking-wide font-bold text-black mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            See The Results Before You Commit
          </h2>
          <p className="text-black/80 text-lg mb-8 max-w-2xl mx-auto">
            Provide one truck or component. We demonstrate full cleaning results on-site to prove 
            effectiveness before any commitment.
          </p>
          <Link to="/contact" className="bg-black text-white px-10 py-4 text-lg uppercase tracking-widest inline-flex items-center gap-2 hover:bg-zinc-900 transition-colors" style={{ fontFamily: 'Bebas Neue, sans-serif' }} data-testid="demo-cta-btn">
            Schedule Your Free Demo
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      {/* Social Proof & Follow Section */}
      <section className="py-16 bg-black border-t border-zinc-800" data-testid="social-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Brand Promise */}
            <div>
              <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2 mb-6 inline-block">
                The 23rd Promise
              </span>
              <h2 className="text-3xl sm:text-4xl uppercase tracking-wide font-bold text-white mb-6" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Results You Can Trust
              </h2>
              <div className="space-y-4">
                {[
                  { stat: '100%', label: 'Satisfaction Guarantee' },
                  { stat: 'Zero', label: 'Secondary Waste Left Behind' },
                  { stat: 'Same Day', label: 'Quote Response Time' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-6 p-4 bg-zinc-950 border border-zinc-800">
                    <span className="text-3xl font-bold text-orange-500" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>{item.stat}</span>
                    <span className="text-zinc-300">{item.label}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Social Media CTA */}
            <div className="bg-zinc-950 border border-zinc-800 p-8">
              <h3 className="text-2xl uppercase tracking-wide font-bold text-white mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Follow Our Work
              </h3>
              <p className="text-zinc-400 mb-6">
                Watch dry ice blasting in action. See real transformations on our social media.
              </p>
              <div className="space-y-3">
                <a 
                  href="https://www.tiktok.com/@23rdindustries" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-black border border-zinc-800 hover:border-orange-500 transition-colors group"
                  data-testid="social-tiktok"
                >
                  <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center group-hover:bg-orange-600 transition-colors">
                    <span className="text-white font-bold text-sm">TT</span>
                  </div>
                  <div>
                    <span className="text-white font-bold">TikTok</span>
                    <p className="text-zinc-500 text-sm">#dryiceblasting #dryicecleaning</p>
                  </div>
                </a>
                <a 
                  href="https://www.instagram.com/23rdindustries" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center gap-4 p-4 bg-black border border-zinc-800 hover:border-orange-500 transition-colors group"
                  data-testid="social-instagram"
                >
                  <div className="w-10 h-10 bg-zinc-900 flex items-center justify-center group-hover:bg-orange-600 transition-colors">
                    <span className="text-white font-bold text-sm">IG</span>
                  </div>
                  <div>
                    <span className="text-white font-bold">Instagram</span>
                    <p className="text-zinc-500 text-sm">Before & After Transformations</p>
                  </div>
                </a>
              </div>
              <p className="text-zinc-500 text-xs mt-6 font-mono">
                DFW's Premier Dry Ice Blasting Service
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <section className="py-12 bg-zinc-950 border-t border-zinc-800" data-testid="trust-badges">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            <div className="text-center">
              <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2">Minority-Owned</span>
            </div>
            <div className="text-center">
              <span className="badge bg-orange-500/10 border border-orange-500/50 text-orange-500 px-4 py-2">Woman-Owned</span>
            </div>
            <div className="text-center">
              <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2">SAM Registered</span>
            </div>
            <div className="text-center">
              <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2">$1M+ Insured</span>
            </div>
            <div className="text-center">
              <span className="badge bg-zinc-900 border border-zinc-800 text-zinc-400 px-4 py-2">15+ Years Experience</span>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};
